import { formatCopy } from '../../copy/format.js';
import copy from '../../copy/en/shell.json' with { type: 'json' };
/**
 * The authenticated layout: left navigation (drawer on small screens), top bar,
 * MFA enrollment banner, and the routed page.
 */
import { Dialog as RadixDialog } from "radix-ui";
import { X } from "lucide-react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { useMe } from "../auth/MeProvider.js";
import { ContextualRecordsProvider } from "../components/ContextualRecords.js";
import { Callout } from "../components/ui/Callout.js";
import { IconButton } from "../components/ui/Button.js";
import { fmtDate } from "../lib/format.js";
import { mfaEnrollmentPending } from "../lib/me.js";
import { SideNav, visibleNavItems } from "./SideNav.js";
import { TopBar } from "./TopBar.js";
import { PageErrorBoundary } from "./PageErrorBoundary.js";
import { DocumentUpdateNotice } from "../pages/document-updates/DocumentUpdateNotice.js";

export function AppShell() {
  const { me } = useMe();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const items = useMemo(
    () => visibleNavItems({ capabilities: me?.capabilities ?? [] }),
    [me?.capabilities],
  );

  // Close the drawer on navigation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <ContextualRecordsProvider><div className="shell">
      <a className="skip-link" href="#main">{copy.AppShell.skipToContent}</a>
      <aside className="sidenav">
        <SideNav items={items} />
      </aside>
      <TopBar onOpenNav={() => setDrawerOpen(true)} />
      <main className="main" id="main" tabIndex={-1}>
        <div className="main__inner stack stack--lg">
          {location.pathname.replace(/\/$/, "") !== "/sales-organization" ? <DocumentUpdateNotice /> : null}
          {mfaEnrollmentPending(me) ? (
            <Callout
              tone="warning"
              title={copy.AppShell.setUpTwoFactorAuthentication}
              action={
                <Link to="/mfa" className="btn btn--primary btn--sm">{copy.AppShell.setUpNow}</Link>
              }
            >{copy.AppShell.staffAccountsRequireAnAuthenticatorApp}{me?.mfa?.graceEndsAt
                ? formatCopy(copy.AppShell.mfaGraceEnds, { date: fmtDate(me.mfa.graceEndsAt) })
                : ""}
            </Callout>
          ) : null}
          <PageErrorBoundary key={location.pathname}>
            <Suspense
              fallback={
                <p role="status" className="muted">{copy.AppShell.loadingPage}</p>
              }
            >
              <Outlet />
            </Suspense>
          </PageErrorBoundary>
        </div>
      </main>

      <RadixDialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <RadixDialog.Portal>
          <RadixDialog.Overlay className="drawer__overlay" />
          <RadixDialog.Content
            className="drawer__content"
            inert={!drawerOpen || undefined}
            aria-hidden={!drawerOpen || undefined}
            aria-describedby={undefined}
          >
            <RadixDialog.Title className="visually-hidden">{copy.AppShell.navigation}</RadixDialog.Title>
            <RadixDialog.Close asChild>
              <IconButton
                icon={X}
                label={copy.AppShell.closeNavigation}
                className="drawer__close"
              />
            </RadixDialog.Close>
            <SideNav items={items} onNavigate={() => setDrawerOpen(false)} />
          </RadixDialog.Content>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    </div></ContextualRecordsProvider>
  );
}
